import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import locationBg from '@assets/images/location-bg.webp';
import { Flex, Heading, Text } from '@radix-ui/themes';
import { Stepper } from '@/components/common';
import { WizardHeader, WizardScreen } from '@/components/layout';
import { ContinueButton } from '@/components/onboarding';
import { locationService } from '@/services';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { getCityCoordinates } from '@/utils/geo';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

type LocationPhase = 'prompt' | 'declined';

export function LocationStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation('onboarding');
  const [phase, setPhase] = useState<LocationPhase>('prompt');
  const setLocationAllowed = useOnboardingStore(state => state.setLocationAllowed);
  const setLatitude = useOnboardingStore(state => state.setLatitude);
  const setLongitude = useOnboardingStore(state => state.setLongitude);
  const city = useOnboardingStore(state => state.city);
  const saveAndNext = useSaveAndNext(flow);

  const fallbackToCityCoordinates = async () => {
    setLocationAllowed(false);
    const coords = await getCityCoordinates(city);
    if (coords) {
      setLatitude(coords.latitude);
      setLongitude(coords.longitude);
    }
    setPhase('declined');
  };

  const allow = async () => {
    const coords = await locationService.getCurrentLocation();
    if (coords) {
      setLocationAllowed(true);
      setLatitude(coords.latitude);
      setLongitude(coords.longitude);
      void saveAndNext();
    } else {
      void fallbackToCityCoordinates();
    }
  };

  const handleBack = () => {
    if (phase === 'declined') {
      setPhase('prompt');
      return;
    }
    flow.back();
  };

  const header = (
    <WizardHeader
      onBack={handleBack}
      centerContent={
        <Stepper progress={flow.progress} step={flow.step} totalSteps={flow.totalSteps} />
      }
    />
  );

  if (phase === 'declined') {
    return (
      <WizardScreen header={header}>
        <Flex direction="column" gap="4" py="2" pb="5" style={{ textAlign: 'center' }}>
          <Heading size="5" weight="light">
            {t('steps.location.declinedTitle')}
          </Heading>
          <Text size="2" color="gray" style={{ maxWidth: '260px', alignSelf: 'center' }}>
            {t('steps.location.declinedDescription')}
          </Text>
          <ContinueButton onClick={() => void saveAndNext()}>
            {t('actions.continue')}
          </ContinueButton>
        </Flex>
      </WizardScreen>
    );
  }

  return (
    <WizardScreen bgPicture={locationBg} header={header}>
      <Flex direction="column" minHeight="0" height="100%" position="relative">
        <Flex
          direction="column"
          justify="center"
          flexGrow="1"
          minHeight="0"
          gap="4"
          style={{ textAlign: 'center' }}
        >
          <Heading size="5" weight="light">
            {t('steps.location.title')}
          </Heading>
          <Text size="2" color="gray" style={{ maxWidth: '280px', alignSelf: 'center' }}>
            {t('steps.location.description')}
          </Text>
          <ContinueButton onClick={() => void allow()}>{t('actions.allowLocation')}</ContinueButton>
        </Flex>
      </Flex>
    </WizardScreen>
  );
}
